import { Box, Grid, List, ListItem, ListItemText, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import BannerReportTIM from './BannerReportTIM'
import EditableText from './EditableText'
import { useContext, useEffect, useState } from 'react'
import { getReportInfoTIMAll } from '../axios/axiosFunctions'
import { GroupContext } from '../contexts/group'
import { Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js'

ChartJS.register(ArcElement, Tooltip);

/* ── Rating helper ── */
const getRating = (positive) => {
    if (positive >= 90) return { label: "STRENGTH", color: "#69AF45" };
    if (positive >= 60) return { label: "DEVELOPING", color: "#2B8DCC" };
    return { label: "OPPORTUNITY", color: "#e67e22" };
};

/* ── Center text plugin for Doughnut ── */
const centerTextPlugin = {
    id: "centerText",
    afterDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = chartArea.bottom - 20;
        const rawValue = chart.config.data.datasets[0].data[0];
        const displayValue = Number.isInteger(rawValue) ? rawValue : rawValue.toFixed(1);
        const ratingColor = chart.config.data.datasets[0].backgroundColor[0];

        ctx.save();
        ctx.font = "bold 28px Tahoma, Arial, sans-serif";
        ctx.fillStyle = ratingColor;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${displayValue}%`, centerX, centerY);
        ctx.restore();
    },
};

/* ── Gauge Card component ── */
const GaugeCard = ({ block }) => {
    const rating = getRating(block.positive);
    const remaining = 100 - block.positive;

    const data = {
        datasets: [{
            data: [block.positive, remaining],
            backgroundColor: [rating.color, "#E8E8E8"],
            borderWidth: 0,
            circumference: 180,
            rotation: -90,
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "75%",
        plugins: {
            tooltip: { enabled: false },
            legend: { display: false },
            datalabels: { display: false },
        },
    };

    return (
        <Paper
            elevation={0}
            sx={{
                padding: "24px 16px 20px",
                borderRadius: "12px",
                textAlign: "center",
                backgroundColor: "#f8fafc",
                border: `2px solid ${rating.color}22`,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: `0 4px 20px ${rating.color}20` },
            }}
        >
            <Box sx={{ position: "relative", width: "100%", height: 120, margin: "0 auto", maxWidth: 200 }}>
                <Doughnut data={data} options={options} plugins={[centerTextPlugin]} />
            </Box>
            <Typography
                variant="subtitle1"
                fontWeight={600}
                color="secondary"
                sx={{ fontSize: "0.95rem", lineHeight: 1.3, minHeight: "2.6em", marginTop: "8px" }}
            >
                {block.name}
            </Typography>
            <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                    color: rating.color,
                    fontSize: "0.85rem",
                    letterSpacing: "0.5px",
                    marginTop: "4px",
                }}
            >
                {rating.label}
            </Typography>
        </Paper>
    );
};

const BlockReportTIMSpecial = ({ onBlocksDataReady, groupId }) => {

    const {currentGroup} = useContext(GroupContext);
    const effectiveGroupId = groupId || currentGroup?._id;
    const [blocks, setBlocks] = useState();
    const [summaryInfo, setSummaryInfo] = useState();

    useEffect(() => {
        const callInfo = async()=>{
            const res = await getReportInfoTIMAll(effectiveGroupId);
            if(res){
                const summaryAll = [res.data.pop()];
                const newSummary = summaryAll.map((b)=>({
                    positive: Number(b.positivePercentage),
                    negative: Number(b.negativePercentage),
                    name:b.name,
                }))
                const newBlocks = res.data
                    // Exclude the senior-leader survey block by slug (works for TIM
                    // and its clones like Executive, where the block is renamed).
                    .filter((b)=> b.slug !== "senior_leader_survey_questions" && b.name !== "Senior Leader Survey Questions")
                    .map((b)=>({
                        positive: Math.round(Number(b.positivePercentage) * 10) / 10,
                        negative: Math.round(Number(b.negativePercentage) * 10) / 10,
                        name:b.name,
                        color:b.color,
                    }));
                setBlocks(newBlocks);
                setSummaryInfo(newSummary);
                if(onBlocksDataReady) {
                    onBlocksDataReady(newBlocks);
                }
            }
        }
        callInfo();
    }, [effectiveGroupId])

    return (
        <>
            <BannerReportTIM text={"Effective Team Practices = High Performance"}/>
            <Box display="flex" flexDirection="column" sx={{padding:{xs:"0px 16px",sm:"0px 24px",md:"0px 48px"}}}>
                <Grid container spacing={2} sx={{margin:"32px 0px 40px 0px"}}>
                    <Grid size={12}>
                        <EditableText fieldKey="special.fiveCategories.intro" variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>The five categories that reflect the practices of high performing teams are as follows:</EditableText>
                        <List sx={{ listStyle: "decimal" }}>
                            <ListItem>
                                <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>1. Culture of Trust & Psychological Safety</Typography>}/>
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>2. Constructive Conversations</Typography>}/>
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>3. Collaborative Decision Making</Typography>}/>
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>4. Cohesive Commitment</Typography>}/>
                            </ListItem>
                            <ListItem>
                                <ListItemText primary={<Typography variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem"}}>5. Clear Communication Inside and Outside the Team</Typography>}/>
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"12px"}} dangerouslySetInnerHTML={{ __html: `Format of the Teams in Motion<sup>&reg;</sup> Team Health Check` }}/>
                        <EditableText fieldKey="special.methodology.body" variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>{`There are 50 total questions on the standard Health Check that are administered to all who take the assessment. Each question asks the team member to rate a statement based on a four-point scale, forcing a positive or negative bent. The rating scale is: Strongly Disagree, Disagree, Agree, Strongly Agree. There is also an option for the respondent to designate "Unable to Reply" on each question. These "Unable to Reply" responses are eliminated from the results calculation. For performance indicators, each team member is asked to rate their perception of team effectiveness in terms of results the team achieves, efficiency of achieving the results, and a rating of the personal experience working on the team.
                        In most companies, we also gather executive-level performance ratings of each team that helps us correlate the specific practices with high-performing teams.`}</EditableText>
                    </Grid>
                    <Grid size={12}>
                        <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"12px"}}>Team Effectiveness Reporting</Typography>
                        <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"8px"}}>Company versus Team</Typography>
                        <EditableText fieldKey="special.companyTeam.body" variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>{`The report can be generated at a company-level, as a compilation of all teams in the company, or at the team level where the assessment responses are reported as a compilation of team members on a particular team.
                        The purpose of the company-level assessment results is to help an organization's senior leaders prioritize organizational development investments. The assessment results will highlight organization-wide developmental needs that can be turned into customized programs for company-wide delivery.
                        The team-level assessment results can be delivered directly to an intact team for purposes of team-specific development and coaching. It is critical the Team Leader be involved in the process of reviewing, interpreting, and sharing the results with the intact team. The team-level results can be turned into a customized team development program including training, in-person team coaching, and customized Team Leader coaching.`}</EditableText>
                        <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"8px",marginTop:"16px"}}>For Developmental Purposes!</Typography>
                        <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>Warning – these results should NOT be used as a performance rating tool for a team, a Team Leader, or an organization! The assessment includes questions around the factors that are proven to drive team effectiveness, and for that reason is designed as a developmental tool to increase the effectiveness of your teams!
                        Please review the results of the report and then engage the support of a Teams In Motion<sup>®</sup> facilitator to complete the interpretation and discuss the plan of action based on the assessment results. An investment in developing these competencies within your teams will lead to measurable performance results! Make the investment count.</Typography>
                        <Typography variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem",marginTop:"8px"}}>Keep in mind that your team is assessed as a full team, not as individual team members.</Typography>
                    </Grid>
                </Grid>
            </Box>
            <BannerReportTIM text={"Team Effectiveness: Team Health Check Summary"}/>
            <Box display="flex" flexDirection="column" sx={{padding:{xs:"0px 16px",sm:"0px 24px",md:"0px 48px"}}}>
                <Grid container spacing={2} sx={{margin:"32px 0px 40px 0px"}}>
                    <Grid size={12}>
                        <EditableText fieldKey="summary.intro" variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>The gauges below reflect your team's overall scores for each category. The percentage shown is the % of positive responses (defined as Agree + Strongly Agree). In the following pages you will find question by question scores.</EditableText>
                        <EditableText fieldKey="summary.levels.intro" variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem",marginTop:"12px"}}>For developmental purposes, we categorize your team's scores into three different levels to help you focus your development priorities.</EditableText>
                        <Box display={"flex"} justifyContent={"center"}>
                            <TableContainer sx={{maxWidth:"550px",margin:"32px 0px",borderRadius:"8px",overflow:"hidden",boxShadow:"0 1px 4px rgba(15,83,133,0.08)"}}>
                                <Table sx={{borderCollapse:'separate',borderSpacing:0,'& .MuiTableCell-root':{border:'none',padding:'12px 20px',fontSize:'0.95rem'}}}>
                                    <TableHead>
                                        <TableRow sx={{backgroundColor:"#0F5385"}}>
                                            <TableCell sx={{fontWeight:"600",color:"white"}}>Score (% of positive responses)</TableCell>
                                            <TableCell sx={{fontWeight:"600",color:"white"}}>Rating</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow sx={{backgroundColor:"#f0f7f0"}}>
                                            <TableCell>90% or higher</TableCell>
                                            <TableCell sx={{fontWeight:600,color:"#69AF45"}}>STRENGTH</TableCell>
                                        </TableRow>
                                        <TableRow sx={{backgroundColor:"#f5f9fc"}}>
                                            <TableCell>60%–89%</TableCell>
                                            <TableCell sx={{fontWeight:600,color:"#2B8DCC"}}>DEVELOPING</TableCell>
                                        </TableRow>
                                        <TableRow sx={{backgroundColor:"#fef8f0"}}>
                                            <TableCell>Less than 60%</TableCell>
                                            <TableCell sx={{fontWeight:600,color:"#e67e22"}}>OPPORTUNITY</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Grid>
                    <Grid size={12}>
                        <Grid container spacing={3} justifyContent="center">
                            {blocks && blocks.map((b, i) => (
                                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <GaugeCard block={b} />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}

export default BlockReportTIMSpecial
